import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  ScaleIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import publicCatalogService, {
  type PublicCatalogResponse,
  type PublicCatalogRule,
} from '../services/publicCatalog.service';
import type { PolicyRule, RuleCategoryKey } from '../types/policy.types';
import RuleCard from '../components/policy/RuleCard';
import LandingHeader from './landing/components/LandingHeader';
import LandingFooter from './landing/components/LandingFooter';

const CATEGORY_ICON: Record<RuleCategoryKey, React.ElementType> = {
  compliance: ShieldCheckIcon,
  legal_risk: ScaleIcon,
  qa: AcademicCapIcon,
};

function publicRuleToPolicyRule(r: PublicCatalogRule): PolicyRule {
  return {
    slug: r.slug,
    name: r.name,
    description_short: r.description_short,
    description_long: r.description_long,
    category: r.category,
    severity: r.severity_default,
    enabled: true,
    regulation_refs: r.regulation_refs,
    params: r.tunable_params.map((p) => ({
      name: p.name,
      label: p.label,
      type: p.type,
      description: p.description,
      unit: p.unit,
      default_value: p.default,
      current_value: p.default,
      is_overridden: false,
    })),
  };
}

export default function HowWeEvaluate() {
  const { t } = useTranslation('policy');
  const [data, setData] = useState<PublicCatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    publicCatalogService
      .getCatalog()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load the rule catalog. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const totalRegulations = useMemo(() => {
    if (!data) return 0;
    const regs = new Set<string>();
    for (const c of data.categories) {
      for (const r of c.rules) {
        for (const ref of r.regulation_refs) {
          regs.add(`${ref.jurisdiction}-${ref.law}`);
        }
      }
    }
    return regs.size;
  }, [data]);

  return (
    <div className="min-h-screen bg-gray-50">
      <LandingHeader />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <header className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl">
            {t('public.title', { defaultValue: 'How CallCov Evaluates Calls' })}
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-base text-gray-600">
            {t('public.subtitle', {
              defaultValue:
                'Every call is scored against the rules below. Your team configures thresholds and disclosures during onboarding — what you see here is the starting point.',
            })}
          </p>
        </header>

        {loading && (
          <div className="flex justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-indigo-600" />
          </div>
        )}

        {error && !loading && (
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">{error}</div>
        )}

        {!loading && !error && data && (
          <>
            <section className="mb-10 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {t('summary.rulesActive', { defaultValue: 'Rules active' })}
                </dt>
                <dd className="mt-1 text-3xl font-bold text-gray-900">{data.total_rules}</dd>
              </div>
              <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {t('summary.regulations', { defaultValue: 'Regulations covered' })}
                </dt>
                <dd className="mt-1 text-3xl font-bold text-gray-900">{totalRegulations}</dd>
              </div>
              <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {t('public.catalogVersion', { defaultValue: 'Catalog version' })}
                </dt>
                <dd className="mt-1 text-3xl font-bold text-gray-900">v{data.catalog_version}</dd>
              </div>
            </section>

            <div className="space-y-10">
              {data.categories.map((cat) => {
                const Icon = CATEGORY_ICON[cat.category];
                if (cat.rules.length === 0) return null;
                return (
                  <section key={cat.category} id={`how-${cat.category}`}>
                    <header className="mb-3 flex items-center gap-2 border-b border-gray-200 pb-2">
                      <Icon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      <h2 className="text-xl font-semibold text-gray-900">{cat.label}</h2>
                      <span className="text-sm text-gray-500">({cat.rules.length})</span>
                    </header>
                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                      {cat.rules.map((r) => (
                        <RuleCard key={r.slug} rule={publicRuleToPolicyRule(r)} />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>

            <section className="mt-12 rounded-2xl bg-indigo-600 px-8 py-10 text-center text-white">
              <h2 className="text-2xl font-bold">
                {t('public.cta.title', { defaultValue: 'Want these tailored to your team?' })}
              </h2>
              <p className="mx-auto mt-2 max-w-2xl text-indigo-100">
                {t('public.cta.subtitle', {
                  defaultValue:
                    'During onboarding we adjust thresholds, add your mandatory disclosures, and pin the regulations that matter to your jurisdiction.',
                })}
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <Link
                  to="/pricing"
                  className="rounded-md bg-white px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
                >
                  {t('public.cta.pricing', { defaultValue: 'See pricing' })}
                </Link>
                <Link
                  to="/contact"
                  className="rounded-md border border-white px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  {t('public.cta.contact', { defaultValue: 'Talk to us' })}
                </Link>
              </div>
            </section>
          </>
        )}
      </main>
      <LandingFooter />
    </div>
  );
}
